/* ─────────────────────────────────────────────────────
   pricing.ts — table de prix Indyanna Studio
   Extensible : ajoutez un modèle dans MODEL_GENDER_MAP
   ───────────────────────────────────────────────────── */

export type Gender = 'homme' | 'femme';
export type DeliveryWeeks = 8 | 14 | 16 | '8mie';

export interface PriceTier {
  minQty: number;
  price: number;
}

export interface DeliveryOption {
  weeks: DeliveryWeeks;
  label: string;
  tiers: PriceTier[];
}

export interface GenderPricing {
  ref: string;
  deliveries: DeliveryOption[];
}

export const PRICING: Record<Gender, GenderPricing> = {
  homme: {
    ref: 'NTH JU',
    deliveries: [
      {
        weeks: 16,
        label: '16 semaines',
        tiers: [
          { minQty: 300,  price: 6.71 },
          { minQty: 500,  price: 5.90 },
          { minQty: 750,  price: 5.29 },
          { minQty: 1000, price: 4.88 },
        ],
      },
      {
        weeks: 14,
        label: '14 semaines',
        tiers: [
          { minQty: 300,  price: 6.80 },
          { minQty: 500,  price: 5.95 },
          { minQty: 750,  price: 5.32 },
          { minQty: 1000, price: 4.94 },
        ],
      },
      {
        weeks: 8,
        label: '8 semaines',
        tiers: [
          { minQty: 100,  price: 10.45 },
          { minQty: 200,  price: 8.23  },
          { minQty: 300,  price: 7.36  },
          { minQty: 500,  price: 6.57  },
          { minQty: 750,  price: 6.02  },
          { minQty: 1000, price: 5.64  },
        ],
      },
      {
        weeks: '8mie',
        label: '8 sem. Made In Europe',
        tiers: [
          { minQty: 30,   price: 23.41 },
          { minQty: 50,   price: 19.22 },
          { minQty: 100,  price: 16.02 },
          { minQty: 200,  price: 14.28 },
          { minQty: 300,  price: 13.44 },
          { minQty: 500,  price: 12.23 },
          { minQty: 750,  price: 11.84 },
          { minQty: 1000, price: 11.06 },
        ],
      },
    ],
  },
  femme: {
    ref: 'NTF JU',
    deliveries: [
      {
        weeks: 16,
        label: '16 semaines',
        tiers: [
          { minQty: 300,  price: 6.36 },
          { minQty: 500,  price: 5.56 },
          { minQty: 750,  price: 4.96 },
          { minQty: 1000, price: 4.56 },
        ],
      },
      {
        weeks: 14,
        label: '14 semaines',
        tiers: [
          { minQty: 300,  price: 6.45 },
          { minQty: 500,  price: 5.61 },
          { minQty: 750,  price: 4.99 },
          { minQty: 1000, price: 4.62 },
        ],
      },
      {
        weeks: 8,
        label: '8 semaines',
        tiers: [
          { minQty: 100,  price: 10.08 },
          { minQty: 200,  price: 7.87  },
          { minQty: 300,  price: 7.01  },
          { minQty: 500,  price: 6.23  },
          { minQty: 750,  price: 5.68  },
          { minQty: 1000, price: 5.32  },
        ],
      },
      {
        weeks: '8mie',
        label: '8 sem. Made In Europe',
        tiers: [
          { minQty: 30,   price: 23.41 },
          { minQty: 50,   price: 19.22 },
          { minQty: 100,  price: 16.02 },
          { minQty: 200,  price: 14.28 },
          { minQty: 300,  price: 13.44 },
          { minQty: 500,  price: 12.23 },
          { minQty: 750,  price: 11.84 },
          { minQty: 1000, price: 11.06 },
        ],
      },
    ],
  },
};

/* ── Mapping modèle → genre ──────────────────────────
   Ajoutez chaque nouveau modèle ici.
   Clé : fileName du modèle (ex. "Tshirt.glb")
   ──────────────────────────────────────────────────── */
export const MODEL_GENDER_MAP: Record<string, Gender> = {
  'Tshirt.glb':       'homme',
  'TshirtFemme.glb':  'femme',
  // Ajoutez vos nouveaux modèles ici :
  // 'NouveauModele.glb': 'homme',
};

export function getModelGender(model: { fileName?: string; category?: string; name?: string } | null): Gender {
  if (!model) return 'homme';

  // 1. Correspondance exacte sur fileName
  if (model.fileName && MODEL_GENDER_MAP[model.fileName]) {
    return MODEL_GENDER_MAP[model.fileName];
  }

  // 2. Mots-clés dans la catégorie ou le nom
  const text = `${model.category || ''} ${model.name || ''}`.toLowerCase();
  if (/femme|woman|female|féminin/.test(text)) return 'femme';
  if (/homme|man\b|male|masculin/.test(text)) return 'homme';

  return 'homme'; // défaut
}

/** Retourne le prix unitaire pour une quantité et un délai donnés, ou null si hors grille */
export function getUnitPrice(gender: Gender, weeks: DeliveryWeeks, qty: number): number | null {
  const delivery = PRICING[gender].deliveries.find(d => d.weeks === weeks);
  if (!delivery) return null;
  const tiers = [...delivery.tiers].sort((a, b) => b.minQty - a.minQty);
  const tier = tiers.find(t => qty >= t.minQty);
  return tier ? tier.price : null;
}
